import React, { useEffect, useState } from 'react';
import InfoModal from '../modals/InfoModal';
import DriversOfInnovationSurvey from '../components/Surveys/DriversOfInnovation/DriversOfInnovationSurvey';
import content from '../data/driversOfInnovationContent.json';
import priorityLevels from '../data/priorityLevels.json';
import Parser from 'html-react-parser';
import Tippy from '@tippyjs/react';
import ModalInnovation from '../modals/ModalInnovation';
import 'tippy.js/dist/tippy.css';

import './DriversOfInnovation.css';


// Accordion Component
const Accordion = ({ title, children }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="accordion-section">
            <div
                className={`accordion-header ${isOpen ? 'open' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
            >
                <h3>{title}</h3>
                <Tippy content={isOpen ? "Click to collapse" : "Click to expand instructions"}>
                    <i 
                        className={`fas ${isOpen ? 'fa-circle-minus' : 'fa-circle-plus'}`}
                        style={{
                            fontSize: '24px',
                            color: '#22c55e'
                        }}
                    ></i>
                </Tippy>
            </div>
            <div className={`accordion-content ${isOpen ? 'open' : ''}`}>
                {children}
            </div>
        </div>
    );
};

function DriversOfInnovation() {
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showRadarModal, setShowRadarModal] = useState(false);
    const [modalData, setModalData] = useState({ title: '', description: '' });
    const [hasAccess, setHasAccess] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [radarModalData] = useState({
        title: 'Completed Innovation Radar',
        content: (
            <div className="image-zoom-container">
                <img
                    src="/assets/img/completed-innovation-radar.png"
                    alt="Completed Innovation Radar"
                />
            </div>
        ),
        hideDefaultContent: true
    });

    const priorityInfo = Object.keys(priorityLevels).reduce((acc, key) => {
        acc[key] = {
            title: priorityLevels[key].title,
            content: <p>{Parser(priorityLevels[key].description)}</p>,
            hideDefaultContent: true
        };
        return acc;
    }, {});

    const innovationInfo = {
        types: {
            title: "Types of Innovation",
            content: <><p>Innovation is the practical implementation of ideas that result in the introduction of <strong>new products</strong>, services, processes, systems or technologies, or the improvement to <strong>existing</strong> products, services, processes, systems or technologies.</p><p>For the purposes of this survey 'Product & Services' shall mean 'products, services, processes, systems and technologies'.</p><h2>Irish Funds Innovation Framework (Innovation Radar)</h2><p>Irish Funds have devised an innovation framework to allow us to understand and describe innovation in a logical manner specific to our industry.</p><h2>Innovation Radar</h2><p>Our innovation radar comprises of three core categories, with a number of sub-categories: </p><ul><li>Sustaining/Incremental Innovation</li><li>Adjacent Innovation</li><li>Emerging/Disruptive Innovation</li></ul></>,
            hideDefaultContent: true
        },
        incremental: {
            title: "Sustaining/Incremental Innovation",
            content: <><p>This is typically where firms spend the majority of their development dollars (80%+). It focuses on the enhancement/improvement/fixing of existing products & services. Typically, product & services definition, ownership, infrastructure, governance models are already in place and well understood by firms.</p><p>In the context of the Fund Industry there are three main sub-categories or Drivers of Innovation within sustaining/incremental innovation category:</p><ol type="a"><li><strong>Business Operations:</strong> typically driven by the business to improve overall operations and are more generally applicable across the business functions e.g. digitisation of processes, process re-engineering, client experience etc.</li><li><strong>Market &amp; Regulation:</strong> typically driven by outside market and regulatory forces e.g. sustainable finance, AML, financial literacy etc.</li><li><strong>Product &amp; Service Maintenance:</strong> driven by the need to improve/enhance existing products &amp; services e.g. ESG data, AIF rulebook updates, ETF transparency.</li></ol></>,
            hideDefaultContent: true
        },
        adjacent: {
            title: "Adjacent Innovation",
            content: <><p>Refers to innovation 'next to' sustaining/incremental innovation and in the context of the fund industry refers to the development of New Product/Service Development utilising existing know-how, capabilities and technologies. Typically, product & service definition, ownership, infrastructure and governance models are less well known, being established or not in place to support the new product or service e.g. development of ELTIF etc</p></>,
            hideDefaultContent: true
        },
        emerging: {
            title: "Emerging/Disruptive Innovation",
            content: <><p>Generally refers to emerging or disruptive products & services (technologies) which may have exponential potential. Typically, product definition is at an ideation or developing stage, accompanied by research, experimentation and product validation initiatives e.g. Gen Ai, Fund Tokenisation, Quantum computing, DTL etc.</p></>,
            hideDefaultContent: true
        }
    };

    useEffect(() => {
        const checkAccess = async () => {
            // Skip access check in development
            if (process.env.NODE_ENV !== 'production') {
                setHasAccess(true);
                setIsLoading(false);
                return;
            }

            // Only check access in production
            const params = new URLSearchParams(window.location.search);
            const emailParam = params.get(process.env.REACT_APP_API_KEY_PARAM);
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            
            if (emailParam && emailRegex.test(emailParam)) {
                setHasAccess(true);
                setIsLoading(false);
                return;
            }

            // Then check IndexedDB
            const request = indexedDB.open('IfiaDB', 1);
            
            request.onsuccess = (event) => {
                const db = event.target.result;
                const transaction = db.transaction(['auth'], 'readonly');
                const store = transaction.objectStore('auth');
                const getEmail = store.get('userEmail');

                getEmail.onsuccess = () => {
                    if (getEmail.result && emailRegex.test(getEmail.result.email)) {
                        setHasAccess(true);
                    } else {
                        setHasAccess(false);
                    }
                    setIsLoading(false);
                };

                getEmail.onerror = () => {
                    setHasAccess(false);
                    setIsLoading(false);
                };
            };

            request.onerror = () => {
                setHasAccess(false);
                setIsLoading(false);
            };
        };

        checkAccess();
    }, []);

    useEffect(() => {
        if (!hasAccess) return; // Skip if no access

        const observerOptions = {
            threshold: 0.15,
            rootMargin: '0px 0px -50px 0px'
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('active');
                }
            });
        }, observerOptions);

        const revealElements = document.querySelectorAll('.reveal');
        revealElements.forEach(element => observer.observe(element));

        return () => {
            revealElements.forEach(element => observer.unobserve(element));
        };
    }, [hasAccess]); // Add hasAccess as dependency

    const handlePriorityClick = (priority) => {
        setModalData(priorityInfo[priority]);
        setShowModal(true);
    };

    const handleInnovationClick = (type) => {
        setModalData(innovationInfo[type]);
        setShowModal(true);
    };

    if (isLoading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh'
            }}>
                Loading...
            </div>
        );
    }

    if (!hasAccess) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                textAlign: 'center',
                padding: '20px'
            }}>
                <div>
                    <h1>Access Denied</h1>
                    <p>You do not have permission to access this page.</p>
                </div>
            </div>
        );
    }

    return (
        <div className='totalPage'>
            <section className="wrapper">
                <div className="container pb-6">
                    <div className="reveal">
                        <p className="survey-description"><h2>Introduction</h2><p>Welcome to Irish Funds industry innovation survey. A comprehensive survey to ascertain the important topics and themes driving your firm and the broader industry. As a senior leader in the industry, we really value and appreciate you taking the time to complete the survey. All responses should be based on your firms 'house position', aligning as closely as possible to your firms' strategic plans.</p><br/><p>Take a few moments before beginning to familiarise yourself with the purpose of the survey and the various how-to guides provided below. There is also a 'Download Topics' button should you wish to distribute the list of topics to others within your firm to support you with completion of the survey.</p></p>
                        
                    </div>
                    <InfoModal
    show={showInfoModal}
    onClose={() => setShowInfoModal(false)}
    title="Data Confidentiality & Privacy"
    data={{
        content: `<strong>Irish Funds Survey Data Confidentiality Assurance</strong>
<br/>
<strong>Confidentiality Commitment</strong><br/>
Irish Funds is dedicated to safeguarding the confidentiality and security of all data collected through our surveys. We ensure the anonymity and protection of your firm's data, emphasising our commitment to maintaining the highest standards of data confidentiality. 
<br/>
<strong>Data Handling</strong><br/>
Survey responses are anonymised at the point of submission, ensuring no direct link between the data provided and the participating firms. This process guarantees the confidentiality of your responses, allowing for the secure/anonymised handling, aggregation and analysis of survey data.
 <br/>
<strong>Privacy Compliance</strong><br/>
In alignment with GDPR and other relevant data protection laws, Irish Funds employs stringent measures to securely process and store personal data. Our practices are transparently detailed in our Privacy Policy.
 <br/>
<strong>Use of Data</strong><br/>
The anonymised data collected is utilised for analytical purposes, contributing to industry insights and enhancing member services. Irish Funds assumes no responsibility for the accuracy of data provided by participants but commits to the confidential handling of all survey responses.
 <br/>
<strong>Amendments and Jurisdiction</strong><br/>
This assurance may be updated to reflect changes in our data handling practices. Participants will be informed of significant amendments. This assurance is governed by the laws of Ireland, ensuring a secure framework for survey participation.
<br/><br/>.`
    }}
/>
                </div>
            </section>

            <section className="wrapper">
                <div className="container image-container">
                    <div className="innovation-definition-wrapper" style={{ position: 'relative' }}>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            marginBottom: '20px'
                        }}>
                            <h2 style={{ margin: 0 }}>How-to Guides</h2>
                            <a
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setShowInfoModal(true);
                                }}
                                style={{
                                    color: 'var(--ifia-blue)',
                                    textDecoration: 'underline',
                                    fontWeight: 'bold'
                                }}
                            >
                                Data Confidentiality & Privacy
                            </a>
                        </div>
                        <div >
                            <div className="videos-container" style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: '20px'
                            }}>

                               
                                {/* Video 1 */}
                                <div className="video-wrapper" style={{ flex: 1, textAlign: 'center' }}>
                                    <video
                                        controls
                                        width="100%"
                                        style={{ maxWidth: '300px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                                    >
                                        <source src="/assets/videos/Video 1 Purpose & Importance of Survey.mp4" type="video/mp4" />
                                        Your browser does not support the video tag.
                                    </video>
                                    <h3 style={{ marginTop: '15px', fontSize: '1rem', fontWeight: 'bold', color: '#2d3748' }}>Purpose & Importance of Survey</h3>
                                </div>

                                {/* Video 2 */}
                                <div className="video-wrapper" style={{ flex: 1, textAlign: 'center' }}>
                                    <video
                                        controls
                                        width="100%"
                                        style={{ maxWidth: '300px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                                    >
                                        <source src="/assets/videos/Video 2 How-to Guide.mp4" type="video/mp4" />
                                        Your browser does not support the video tag.
                                    </video>
                                    <h3 style={{ marginTop: '15px', fontSize: '1rem', fontWeight: 'bold', color: '#2d3748' }}>How-to Guide</h3>
                                </div>

                                {/* Video 3 */}
                                <div className="video-wrapper" style={{ flex: 1, textAlign: 'center' }}>
                                    <video
                                        controls
                                        width="100%"
                                        style={{ maxWidth: '300px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                                    >
                                        <source src="/assets/videos/Video 3 Priority Levels.mp4" type="video/mp4" />
                                        Your browser does not support the video tag.
                                    </video>
                                    <h3 style={{ marginTop: '15px', fontSize: '1rem', fontWeight: 'bold', color: '#2d3748' }}>Priority Levels</h3>
                                </div>

                                {/* Video 4 */}
                                <div className="video-wrapper" style={{ flex: 1, textAlign: 'center' }}>
                                    <video
                                        controls
                                        width="100%"
                                        style={{ maxWidth: '300px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                                    >
                                        <source src="/assets/videos/Video 4 Innovation Definition.mp4" type="video/mp4" />
                                        Your browser does not support the video tag.
                                    </video>
                                    <h3 style={{ marginTop: '15px', fontSize: '1rem', fontWeight: 'bold', color: '#2d3748' }}>Innovation Definition</h3>
                                </div>
                            </div>
                        </div>

                        <h2>Innovation Radar</h2>   

                        <img src='/assets/img/innovation-definition.png' alt="Innovation Definition" />



                        {/* Types of Innovation Hotspot */}
                        <div
                            className="hotspot"
                            onClick={() => handleInnovationClick('types')}
                            style={{
                                position: 'absolute',
                                left: '20%',
                                top: '79%',
                                cursor: 'pointer',
                                background: 'rgba(0, 0, 0, 0.7)',
                                border: '2px solid white',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: 'bold',
                                transition: 'all 0.3s ease',
                                zIndex: 10
                            }}>
                            <i className="fas fa-info"></i>
                        </div>

                        {/* Incremental/Sustaining Hotspot */}
                        <div
                            className="hotspot"
                            onClick={() => handleInnovationClick('incremental')}
                            style={{
                                position: 'absolute',
                                left: '15%',
                                top: '95%',
                                cursor: 'pointer',
                                background: 'rgba(0, 0, 0, 0.7)',
                                border: '2px solid white',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: 'bold',
                                transition: 'all 0.3s ease',
                                zIndex: 10
                            }}>
                            <i className="fas fa-info"></i>
                        </div>

                        {/* Adjacent Hotspot */}
                        <div
                            className="hotspot"
                            onClick={() => handleInnovationClick('adjacent')}
                            style={{
                                position: 'absolute',
                                left: '76%',
                                top: '79%',
                                cursor: 'pointer',
                                background: 'rgba(0, 0, 0, 0.7)',
                                border: '2px solid white',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: 'bold',
                                transition: 'all 0.3s ease',
                                zIndex: 10
                            }}>
                            <i className="fas fa-info"></i>
                        </div>

                        {/* Emerging/Disruptive Hotspot */}
                        <div
                            className="hotspot"
                            onClick={() => handleInnovationClick('emerging')}
                            style={{
                                position: 'absolute',
                                left: '95.5%',
                                top: '79%',
                                cursor: 'pointer',
                                background: 'rgba(0, 0, 0, 0.7)',
                                border: '2px solid white',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: 'bold',
                                transition: 'all 0.3s ease',
                                zIndex: 10
                            }}>
                            <i className="fas fa-info"></i>
                        </div>
                    </div>
                </div>

                <div className="container mt-4">
                    <div className="reveal">
                        <div className="instructions-accordion">
                            <Accordion title="More information?">
                                <div className="reveal">
                                    <div className="instructions-container">
                                        <p className="survey-description">{Parser(content.instructionsLine1)}</p>
                                        <ul className="priority-list">
                                            <li>
                                                <Tippy content={priorityLevels.high.description}>
                                                    <span onClick={() => handlePriorityClick('high')} style={{ cursor: 'pointer' }}>High</span>
                                                </Tippy>
                                            </li>
                                            <li>
                                                <Tippy content={priorityLevels.medium.description}>
                                                    <span onClick={() => handlePriorityClick('medium')} style={{ cursor: 'pointer' }}>Medium</span>
                                                </Tippy>
                                            </li>
                                            <li>
                                                <Tippy content={priorityLevels.low.description}>
                                                    <span onClick={() => handlePriorityClick('low')} style={{ cursor: 'pointer' }}>Low</span>
                                                </Tippy>
                                            </li>
                                            <li>
                                                <Tippy content={priorityLevels.none.description}>
                                                    <span onClick={() => handlePriorityClick('none')} style={{ cursor: 'pointer' }}>No Priority</span>
                                                </Tippy>
                                            </li>
                                        </ul>
                                        <p className="survey-description">{Parser(content.instructionsLine2)}</p>

                                        <div className="text-center my-4">
                                            <button
                                                onClick={() => setShowRadarModal(true)}
                                                className="view-radar-btn"
                                                style={{
                                                    padding: '12px 24px',
                                                    backgroundColor: '#2c5282',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    fontSize: '1rem',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '8px'
                                                }}
                                                onMouseOver={(e) => e.target.style.backgroundColor = '#2a4365'}
                                                onMouseOut={(e) => e.target.style.backgroundColor = '#2c5282'}
                                            >
                                                <i className="fas fa-chart-radar" style={{ fontSize: '1.2rem' }}></i>View sample completed Innovation Radar</button>
                                        </div>

                                        <p style={{ marginTop: '20px' }} className="survey-description">{Parser(content.instructionsLine3)}</p>
                                    </div>
                                </div>
                            </Accordion>
                        </div>
                    </div>
                </div>
            </section>

            <section className="wrapper">
                <div className="container" id="definition">
                    <DriversOfInnovationSurvey />
                </div>
            </section>

            <ModalInnovation
                show={showModal}
                onClose={() => setShowModal(false)}
                data={modalData}
                small={true}
                showImage={false}
            />

            <ModalInnovation
                show={showRadarModal}
                onClose={() => setShowRadarModal(false)}
                data={radarModalData}
                allowFullscreen={true}
                small={false}
                showImage={false}
            />
        </div>
    );
}

export default DriversOfInnovation; 